import { useState, useCallback } from 'react';
import { ParkingTestViewModel } from '../../../viewmodels/ParkingTestViewModel';
import { getErrorMessage } from '../../../types/error';
import { LearningHistoryItem, ParkingTestState } from '../types';

interface UseLearningWorkflowProps {
  projectId: string;
  state: ParkingTestState;
  setState: React.Dispatch<React.SetStateAction<ParkingTestState>>;
  loadLearningHistory: () => Promise<void>;
}

export function useLearningWorkflow({
  projectId,
  state,
  setState,
  loadLearningHistory,
}: UseLearningWorkflowProps) {
  const [selectedHistory, setSelectedHistory] = useState<LearningHistoryItem | null>(null);
  const [historyResultLoading, setHistoryResultLoading] = useState(false);

  const handleStartLearning = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const result = await ParkingTestViewModel.startLearning(
        projectId,
        state.selectedLearningFolder,
        state.selectedRoiFile,
        state.selectedTestFolder,
        state.varThreshold,
        state.learningRate,
        state.iterations
      );

      setState(prev => ({
        ...prev,
        learningResult: result,
        lastLearningFolderPath: result.folder_path
      }));

      if (result.folder_path) {
        const detailedResults = await ParkingTestViewModel.loadLearningResults(projectId, result.folder_path);

        if (detailedResults) {
          setState(prev => ({
            ...prev,
            learningResultsData: detailedResults,
            showResults: true,
            lastLearningFolderPath: result.folder_path
          }));
        } else {
          setState(prev => ({
            ...prev,
            error: '학습 결과를 불러오는데 실패했습니다.'
          }));
        }
      }

      await loadLearningHistory();
    } catch (error) {
      setState(prev => ({ ...prev, error: getErrorMessage(error) }));
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [projectId, state.selectedLearningFolder, state.selectedRoiFile, state.selectedTestFolder, state.varThreshold, state.learningRate, state.iterations, setState, loadLearningHistory]);

  const handleHistorySelect = useCallback(async (historyItem: LearningHistoryItem) => {
    try {
      setHistoryResultLoading(true);
      setSelectedHistory(historyItem);

      setState(prev => ({
        ...prev,
        selectedHistoryResults: null
      }));

      const detailedResults = await ParkingTestViewModel.loadLearningResults(projectId, historyItem.folder_path);

      setState(prev => ({
        ...prev,
        selectedHistoryResults: detailedResults
      }));
    } catch (error) {
      console.error('히스토리 결과 로드 실패:', error);
      setState(prev => ({
        ...prev,
        selectedHistoryResults: null,
        error: '히스토리 결과를 불러오는데 실패했습니다.'
      }));
    } finally {
      setHistoryResultLoading(false);
    }
  }, [projectId, setState]);

  return {
    selectedHistory,
    historyResultLoading,
    handleStartLearning,
    handleHistorySelect,
  };
}
